export function asRows(value) {
  return Array.isArray(value) ? value : [];
}

const APPLICATION_PREFILL_NOISE = new Set(['sap', 'the', 'a', 'an', 'fiori', 'application', 'app', 'please']);

/** Do not put an introductory word into the application field. */
export function catalogApplicationPrefill(value) {
  const text = String(value || '').trim();
  if (!text || APPLICATION_PREFILL_NOISE.has(text.toLowerCase())) return '';
  return text;
}

const CLARIFICATION_FIELDS = ['target_role', 'application', 'source_configuration', 'access_strategy', 'copy_source', 'catalog', 'operation', 'note'];

export function catalogClarificationView(clarification = {}) {
  const explicit = Array.isArray(clarification.required_fields) && clarification.required_fields.length > 0;
  const required = new Set(explicit ? clarification.required_fields : ['target_role']);
  const optional = new Set(
    Array.isArray(clarification.optional_fields)
      ? clarification.optional_fields
      : (explicit ? ['note'] : ['application', 'catalog', 'note'])
  );
  const known = Array.isArray(clarification.known) ? clarification.known.filter((row) => row?.value) : [];
  const knownFields = new Set(known.map((row) => row.field));
  const presence = (name) => {
    if (knownFields.has(name)) return 'known';
    if (required.has(name)) return 'required';
    if (optional.has(name)) return 'optional';
    return 'hidden';
  };
  const fields = Object.fromEntries(CLARIFICATION_FIELDS.map((name) => [name, presence(name)]));
  if (fields.note === 'hidden') fields.note = 'optional';
  return {
    ...fields,
    known,
    showEvidence: clarification.evidence === 'authorization',
  };
}

export function catalogChoiceField(clarification = {}) {
  const explicit = clarification.candidate_field || asRows(clarification.required_fields)[0] || '';
  return CLARIFICATION_FIELDS.includes(explicit) ? explicit : '';
}

export function catalogSelectionReady(clarification = {}, infoForm = {}) {
  const field = catalogChoiceField(clarification);
  const candidates = asRows(clarification.candidates).map((value) => String(value));
  if (!field || candidates.length === 0) return true;
  const selected = String(infoForm[field] || '').trim();
  if (!candidates.includes(selected)) return false;
  const details = asRows(clarification.candidate_details);
  if (field === 'source_configuration') {
    const row = details.find((item) => item.configuration_id === selected);
    if (row && row.metadata_complete === false) return false;
  }
  if (field === 'access_strategy') {
    const row = details.find((item) => item.strategy_id === selected);
    if (row && row.execution_readiness === 'blocked') return false;
  }
  return true;
}

export function catalogStepCopy(clarification = {}, { approveOnSelect = false } = {}) {
  const count = asRows(clarification.candidates).length;
  if (approveOnSelect && clarification.question === 'access_strategy') {
    return {
      title: 'How should HALO provide this application?',
      description: 'Compare the catalogs. Approve & apply records your approval for the selected catalog. HALO rechecks SAP before writing and stops for a separate approval if SAP changed or another role uses the catalog. Authorization changes are approved later.',
    };
  }
  if (approveOnSelect && clarification.question === 'copy_source') {
    return {
      title: 'Which catalog should HALO copy from?',
      description: 'These source catalogs differ in a way that can change the application. Compare the difference on each option. Approve & apply continues your approval for the catalog you already selected.',
    };
  }
  if (clarification.question === 'application_identity') {
    return {
      title: 'Which Fiori application do you need?',
      description: count === 2
        ? 'SAP found two different application identities. Compare the application id, title, and intent. A similar name is not the same application, and this is not a catalog choice.'
        : `SAP found ${count} different application identities. Compare the application id, title, and intent. This is not a catalog choice.`,
    };
  }
  if (clarification.question === 'application_request') {
    return {
      title: 'Which application title should HALO look up?',
      description: 'The request names more than one application title. Select one. This is not a catalog choice and not an SAP identity match.',
    };
  }
  if (clarification.question === 'application_unparsed') {
    return {
      title: 'What is the Fiori application title?',
      description: 'The application title could not be read from the request. Enter the full title. Words such as SAP or the are not the application name.',
    };
  }
  if (clarification.question === 'application_unverified') {
    return {
      title: 'Which SAP application identity is this?',
      description: 'The extracted title was not verified as one SAP application. Provide the application id or another exact identity. HALO will not pick a similar name.',
    };
  }
  if (clarification.question === 'source_configuration') {
    if (clarification.mapping_decision === 'missing_evidence') {
      return {
        title: 'Mapping evidence is incomplete',
        description: 'A catalog is missing its tile or target mapping, so it cannot be used as a copy source. Select a complete mapping. Assigning a whole catalog is a separate strategy and does not repair this mapping. Navigation parameters that were not returned remain unverified.',
        warning: true,
      };
    }
    return {
      title: 'Which application mapping should HALO use?',
      description: 'These mappings differ. Compare the semantic action, tile, target mapping, and navigation parameters, then select one. This selection does not approve a SAP change.',
    };
  }
  if (clarification.question === 'access_strategy') {
    return {
      title: 'How should HALO provide this application?',
      description: 'Compare the catalogs. Adding an application to a catalog, and creating a dedicated catalog, stay proposal-only. Assigning an existing catalog can be approved separately and does not grant backend authorization. This choice does not approve a SAP change.',
    };
  }
  if (clarification.question === 'copy_source') {
    return {
      title: 'Which catalog should HALO copy from?',
      description: 'These source catalogs differ in a way that can change the application. Compare the difference on each option. Catalog-local ids alone are not a reason to choose. This does not approve a SAP change or verify missing evidence.',
    };
  }
  if (clarification.question === 'destination_catalog') {
    return {
      title: 'Which catalog should receive this application?',
      description: 'Choose the destination. A content change can affect every role that uses the catalog.',
    };
  }
  return null;
}

export function workflowInfoError(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const text = detail.map((item) => item?.msg || item).filter((item) => typeof item === 'string' && item.trim()).join(' ');
    if (text) return text;
  }
  if (detail && typeof detail === 'object') {
    const text = detail.message || detail.error;
    if (typeof text === 'string' && text.trim()) return text.trim();
  }
  if (error?.response?.status) return `The information was not saved (HTTP ${error.response.status}).`;
  return 'The information was not saved. Select the requested choice and try again.';
}

export function catalogCandidateLines(clarification = {}) {
  return asRows(clarification.candidate_details).map((row) => {
    if (row.configuration_id) {
      const parts = [];
      if (row.recommended) parts.push('Recommended');
      parts.push(row.fiori_id, [row.semantic_object, row.semantic_action].filter(Boolean).join('/'));
      const sources = asRows(row.source_catalogs).filter(Boolean);
      if (sources.length) parts.push(`source catalogs ${sources.join(', ')}`);
      if (row.tile_id || row.tile_id_stable) parts.push(`tile ${row.tile_id_stable || row.tile_id}`);
      if (row.target_mapping_id || row.target_mapping_id_stable) {
        parts.push(`target mapping ${row.target_mapping_id_stable || row.target_mapping_id}`);
      }
      if (row.suitability) parts.push(row.suitability);
      return parts.filter(Boolean).join(' · ');
    }
    if (row.fiori_id && !asRows(row.mappings).length && !row.suitability) {
      const sources = asRows(row.source_catalogs).filter(Boolean);
      if (!sources.length && row.source_catalog) sources.push(row.source_catalog);
      const identity = [row.fiori_id, row.title].filter(Boolean);
      if (row.intent) identity.push(row.intent);
      else if (row.semantic_object || row.semantic_action) {
        identity.push(`${row.semantic_object || ''}/${row.semantic_action || ''}`);
      }
      if (row.sapui5_component) identity.push(row.sapui5_component);
      if (row.tile_id) identity.push(`tile ${row.tile_id}`);
      if (row.target_mapping_id) identity.push(`target mapping ${row.target_mapping_id}`);
      if (sources.length) identity.push(`source catalogs ${sources.join(', ')}`);
      if (row.recommended) identity.unshift('Recommended');
      return identity.join(' · ');
    }
    const mapping = asRows(row.mappings)[0] || {};
    const parts = [];
    if (row.recommended) parts.push('Recommended');
    if (row.catalog_id) parts.push(row.catalog_id);
    if (mapping.semantic_object || mapping.semantic_action) {
      parts.push(`${mapping.semantic_object || ''}/${mapping.semantic_action || ''}`);
    }
    if (mapping.fiori_id) parts.push(mapping.fiori_id);
    if (mapping.title) parts.push(mapping.title);
    if (row.suitability) parts.push(row.suitability);
    if (row.shared_role_impact) parts.push(row.shared_role_impact);
    if (row.change_kind === 'content_mapping') {
      parts.push('catalog-content mapping, separate from assigning a catalog to a role');
    }
    const others = asRows(row.other_applications).filter(Boolean);
    if (others.length && !row.suitability) parts.push(`also exposes ${others.join(', ')}`);
    if (row.service_entry_count) {
      parts.push(`${row.service_entry_count} service entries are separate from the tile mapping`);
    }
    if (row.already_assigned) parts.push('already on the role');
    const scope = row.provenance?.scope || mapping.provenance?.scope;
    if (scope) parts.push(scope);
    if (row.provenance?.live_sap === false || mapping.provenance?.live_sap === false || row.simulated === true) {
      parts.push('SIMULATED');
    }
    return parts.join(' · ');
  });
}

const OPERATION_LABELS = {
  CATALOG_ADD: 'Add catalog to role',
  CATALOG_REMOVE: 'Remove catalog from role',
  CATALOG_REFRESH: 'Selective catalog refresh',
  APPLICATION_TO_CATALOG: 'Add application to a catalog already on the role',
  DEDICATED_CATALOG_CREATE: 'Create a dedicated catalog',
  VERIFY_CATALOG_ASSIGNMENT: 'Verify catalog assignment',
  VERIFY_ALREADY_ASSIGNED: 'Catalog already assigned',
  VERIFY_ALREADY_AVAILABLE: 'Application already available',
  GATEWAY_SERVICE_ADD: 'Add OData service to role',
  GATEWAY_SERVICE_REMOVE: 'Remove OData service from role',
  VERIFY_ALREADY_PRESENT: 'OData service already on role',
};

export function operationLabel(operation) {
  return OPERATION_LABELS[operation] || operation || '';
}

export function isServiceProposal(proposal = {}) {
  const operation = String(proposal.operation || '');
  return operation.startsWith('GATEWAY_SERVICE') || operation === 'VERIFY_ALREADY_PRESENT'
    || Boolean(proposal.service_identity);
}

export function serviceIdentity(proposal = {}) {
  const identity = proposal.service_identity || {};
  const payload = proposal.change_payload || {};
  return {
    objectType: identity.object_type || payload.ObjectType || payload.object_type || '',
    name: identity.obj_name || payload.ObjName || payload.obj_name || '',
    version: identity.service_version || payload.ServiceVersion || payload.service_version || '',
  };
}

const CONTENT_TYPE_LABELS = {
  TARGET_MAPPING: 'target mapping',
  STATIC_TILE: 'static tile',
  DYNAMIC_TILE: 'dynamic tile',
  LAUNCHPAD_CHIP: 'launchpad CHIP',
};

function contentType(row = {}) {
  const objectType = String(row.object_type || '').trim().toUpperCase();
  if (objectType) return objectType;
  return CONTENT_TYPE_LABELS[row.content_type] || 'type not returned by SAP';
}

export function contentLabel(row = {}) {
  const name = row.title || row.application || row.obj_name || '';
  return [`[${contentType(row)}]`, name, row.service_version].filter(Boolean).join(' ');
}

export function contentTypeCounts(rows = []) {
  const counts = {};
  asRows(rows).forEach((row) => {
    const kind = contentType(row || {});
    counts[kind] = (counts[kind] || 0) + 1;
  });
  return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
}

const ALREADY_SATISFIED = new Set([
  'VERIFY_ALREADY_ASSIGNED',
  'VERIFY_ALREADY_AVAILABLE',
  'VERIFY_ALREADY_PRESENT',
]);

const PROPOSAL_ONLY_OPERATIONS = new Set([
  'APPLICATION_TO_CATALOG',
  'DEDICATED_CATALOG_CREATE',
]);

const READ_ONLY_OPERATIONS = new Set([
  'VERIFY_CATALOG_ASSIGNMENT',
]);

export function isSimulatedProposal(proposal = {}, execution = {}) {
  return proposal.simulated === true
    || proposal.provenance?.live_sap === false
    || execution?.simulated === true
    || String(proposal.source || '').toLowerCase() === 'simulated';
}

/** '' | already_satisfied | read_only | proposal_only | unsupported */
export function reviewDisposition(proposal = {}) {
  const operation = String(proposal.operation || '');
  if (ALREADY_SATISFIED.has(operation)) return 'already_satisfied';
  if (READ_ONLY_OPERATIONS.has(operation)) return 'read_only';
  if (operation === 'APPLICATION_TO_CATALOG' && proposal.writer_readiness?.ready === true) return '';
  const blocker = String(proposal.execution_blocker || '').toUpperCase();
  const support = String(proposal.support || '').toLowerCase();
  if (
    support === 'unsupported'
    || blocker.includes('UNSUPPORTED')
    || blocker === 'WRITE_NOT_ACTIVATED'
    || blocker.includes('IWSG')
  ) {
    return 'unsupported';
  }
  if (
    proposal.executable === false
    || support === 'proposal_only'
    || PROPOSAL_ONLY_OPERATIONS.has(operation)
  ) {
    return 'proposal_only';
  }
  return '';
}

/** Whether the catalog chosen at selection can be approved without a second screen. */
export function selectionApprovalVerdict(ticket, intent = {}) {
  const state = ticket?.workflow_state || {};
  const change = state.menu_change || {};
  const proposal = change.proposal;
  const stage = String(ticket?.workflow_stage || '');
  if (!proposal) {
    const question = state.clarification?.question;
    if (['FAILED', 'ESCALATED', 'REJECTED'].includes(stage)) {
      return { action: 'stop', reason: 'HALO could not build the catalog proposal. Nothing was approved.' };
    }
    if (stage === 'NEEDS_INFO' && question && question !== intent.question && question !== 'copy_source') {
      return { action: 'stop', reason: 'HALO needs another answer before the catalog change can be approved.' };
    }
    return { action: 'wait' };
  }
  if (intent.priorPayloadHash && proposal.payload_hash === intent.priorPayloadHash) {
    return { action: 'wait' };
  }
  if (change.lifecycle !== 'PENDING_APPROVAL') {
    return { action: 'stop', reason: '' };
  }
  const review = catalogReview(proposal);
  const separate = (reason) => ({ action: 'stop', reason: `${reason} Review the proposal and approve it separately.` });
  if (proposal.operation !== 'APPLICATION_TO_CATALOG') return separate('This is not a catalog content change.');
  if (isSimulatedProposal(proposal, change.execution || {})) return separate('The proposal is simulated.');
  if (proposal.writer_readiness?.ready !== true || proposal.execution_blocker || reviewDisposition(proposal) !== '') {
    return separate('The catalog writer is not ready for this proposal.');
  }
  if (intent.catalog && review.destinationCatalog !== intent.catalog) {
    return separate(`HALO proposed ${review.destinationCatalog || 'another catalog'}, not ${intent.catalog}.`);
  }
  const otherRole = review.affectedRoles.some((role) => role !== proposal.role);
  if (review.roleCount !== 1 || otherRole) {
    return separate('More than one role uses this catalog, or the role count is not confirmed.');
  }
  if (!proposal.payload_hash || !proposal.menu_state_hash || !review.reviewHash) {
    return separate('The proposal is missing its approval binding.');
  }
  return {
    action: 'approve',
    hashes: {
      payloadHash: proposal.payload_hash,
      menuStateHash: proposal.menu_state_hash,
      reviewHash: review.reviewHash,
    },
  };
}

function applicationLabel(proposal, source) {
  const fioriId = String(
    source.fiori_id || proposal.change_payload?.FioriId || proposal.source_configuration?.fiori_id || ''
  ).trim();
  const title = String(proposal.source_configuration?.title || source.title || '').trim();
  const requested = String(proposal.requested_application || proposal.application || '').trim();
  const isFioriId = (value) => Boolean(fioriId) && value.localeCompare(fioriId, undefined, { sensitivity: 'accent' }) === 0;
  if (title && !isFioriId(title)) return title;
  if (requested && !isFioriId(requested)) return requested;
  return title || requested;
}

export function catalogReview(proposal = {}) {
  const impact = proposal.catalog_impact || {};
  const roles = asRows(impact.roles).map((row) => row?.role || row).filter(Boolean);
  const delta = proposal.content_delta || {};
  const source = proposal.physical_source || delta.tile || {};
  const roleCount = Number.isInteger(impact.role_count) ? impact.role_count : null;
  const countKnown = impact.complete === true || impact.impact_complete === true;
  return {
    hasImpact: Boolean(proposal.catalog_impact),
    rolesScanned: impact.roles_scanned,
    scanTruncated: impact.role_scan_truncated === true,
    failedRoleReads: asRows(impact.failed_role_reads),
    application: applicationLabel(proposal, source),
    fioriId: source.fiori_id || proposal.change_payload?.FioriId || proposal.source_configuration?.fiori_id || '',
    tcode: proposal.requested_tcode || proposal.tcode || '',
    catalog: proposal.catalog_id
      || proposal.change_payload?.CatalogId
      || proposal.change_payload?.catalog_id
      || delta.destination_catalog
      || '',
    sourceCatalog: source.source_catalog || source.catalog_id || (proposal.source_catalogs || [])[0] || '',
    destinationCatalog: delta.destination_catalog || proposal.catalog_id || '',
    tileId: source.tile_id || proposal.change_payload?.TileId || '',
    targetMappingId: source.target_mapping_id || proposal.change_payload?.TargetMappingId || '',
    navigationVerified: source.navigation_parameters_verified === true,
    roleCount: countKnown ? roleCount : null,
    transportPlan: proposal.transport_plan || null,
    roleMaintenancePlan: proposal.role_maintenance_plan || null,
    additionalApplications: asRows(proposal.additional_applications),
    mappings: asRows(proposal.mappings).length
      ? asRows(proposal.mappings)
      : asRows(proposal.before?.matched_tiles),
    affectedRoles: roles,
    impactComplete: countKnown && (roleCount !== null || roles.length > 0 || impact.impact_complete !== false),
    before: proposal.before || null,
    after: proposal.after || null,
    blocker: proposal.execution_blocker || '',
    reviewHash: proposal.review_hash || '',
  };
}
